import { elevation } from '../elevation';

const colors = {
  surface: {
    ink: '#fafafa',
    onPrimary: '#171717',
    body: '#a1a1a1',
    mute: '#888888',
    hairline: '#2e2e2e',
    hairlineStrong: '#525252',
    canvas: '#171717',
    canvasSoft: '#0a0a0a',
    canvasSoft2: '#141414',
    link: '#3291ff',
    linkDeep: '#0761d1',
    linkBgSoft: '#0d2847',
    error: '#ff5555',
    errorSoft: '#3d1518',
    errorDeep: '#c50000',
    warning: '#f5a623',
    warningSoft: '#3d2e14',
    warningDeep: '#ab570a',
    violet: '#a855f7',
    violetSoft: '#2e1a47',
    violetDeep: '#7928ca',
    cyan: '#50e3c2',
    cyanSoft: '#0d3d34',
    cyanDeep: '#29bc9b',
    highlightPink: '#ff0080',
    highlightMagenta: '#eb367f',
    selectionBg: '#fafafa',
    selectionFg: '#171717',
    gradientDevelopStart: '#007cf0',
    gradientDevelopEnd: '#00dfd8',
    gradientPreviewStart: '#7928ca',
    gradientPreviewEnd: '#ff0080',
    gradientShipStart: '#ff4d4d',
    gradientShipEnd: '#f9cb28',
  },
  primary: {
    50: '#1a1a1a',
    100: '#262626',
    200: '#333333',
    300: '#525252',
    400: '#888888',
    500: '#fafafa',
    600: '#e5e5e5',
    700: '#d4d4d4',
    800: '#a1a1a1',
    900: '#171717',
  },
  brand: {
    50: '#1e1533',
    100: '#2a1f4d',
    200: '#3d2e6b',
    300: '#5a45a3',
    400: '#7c6ae8',
    500: '#8b7cf8',
    600: '#6A5CFF',
    700: '#5a4ce6',
    800: '#4a3dcc',
    900: '#322899',
  },
  secondary: {
    50: '#1f1529',
    100: '#2d1f42',
    200: '#3d2a5c',
    300: '#5c3d8a',
    400: '#8b5cf6',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
  },
  success: {
    50: '#0d2e28',
    100: '#134e42',
    200: '#166f5c',
    300: '#1d9a82',
    400: '#2dd4bf',
    500: '#29bc9b',
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
  },
  bookmark: { saved: '#D4A017', savedMuted: '#B8860B' },
  warning: {
    50: '#2e2410',
    100: '#3d3014',
    200: '#5c4520',
    300: '#8a6628',
    400: '#fbbf24',
    500: '#f5a623',
    600: '#d97706',
    700: '#ab570a',
    800: '#92400e',
    900: '#78350f',
  },
  danger: {
    50: '#2e1515',
    100: '#3d1a1a',
    200: '#5c2828',
    300: '#8a3d3d',
    400: '#f87171',
    500: '#ff5555',
    600: '#ee0000',
    700: '#c50000',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  info: {
    50: '#0d2847',
    100: '#123a66',
    200: '#1a4f8c',
    300: '#2563eb',
    400: '#3b82f6',
    500: '#3291ff',
    600: '#0761d1',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  accent: {
    50: '#0d2847',
    100: '#123a66',
    200: '#1a4f8c',
    300: '#2563eb',
    400: '#3b82f6',
    500: '#3291ff',
    600: '#0761d1',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  gray: {
    50: '#1a1a1a',
    100: '#262626',
    200: '#333333',
    300: '#525252',
    400: '#888888',
    500: '#a1a1a1',
    600: '#d4d4d4',
    700: '#e5e5e5',
    800: '#f5f5f5',
    900: '#fafafa',
  },
  background: {
    primary: '#0a0a0a',
    secondary: '#171717',
    tertiary: '#141414',
    card: '#171717',
    elevated: '#1f1f1f',
  },
  border: {
    light: '#2e2e2e',
    medium: '#404040',
    dark: '#525252',
    accent: '#fafafa',
  },
  text: {
    primary: '#fafafa',
    secondary: '#a1a1a1',
    tertiary: '#888888',
    muted: '#888888',
    inverse: '#171717',
    accent: '#3291ff',
  },
  sidebar: {
    bg: '#171717',
    activeBg: '#262626',
    activeIndicator: '#fafafa',
    inactiveText: '#a1a1a1',
    activeText: '#fafafa',
    sectionHeader: '#888888',
    badgeBg: '#fafafa',
    logoIconBg: '#fafafa',
    userCardBg: '#141414',
    separator: '#2e2e2e',
  },
  header: {
    bg: '#171717',
    searchBg: '#141414',
    searchPlaceholder: '#888888',
    border: '#2e2e2e',
  },
  mainContent: { bg: '#0a0a0a' },
  map: {
    ocean: '#0a0a0a',
    land: '#333333',
    landHover: '#525252',
    stroke: '#2e2e2e',
    shimmerBase: '#1a1a1a',
    shimmerMid: '#333333',
    shimmerHighlight: '#525252',
  },
  elevation,
};

const cssVariables = {
  '--cs-bg-primary': colors.background.primary,
  '--cs-bg-secondary': colors.background.secondary,
  '--cs-bg-tertiary': colors.background.tertiary,
  '--cs-bg-card': colors.background.card,
  '--cs-bg-elevated': colors.background.elevated,
  '--cs-border-light': colors.border.light,
  '--cs-border-medium': colors.border.medium,
  '--cs-border-dark': colors.border.dark,
  '--cs-border-accent': colors.border.accent,
  '--cs-text-primary': colors.text.primary,
  '--cs-text-secondary': colors.text.secondary,
  '--cs-text-tertiary': colors.text.tertiary,
  '--cs-text-muted': colors.text.muted,
  '--cs-text-inverse': colors.text.inverse,
  '--cs-text-accent': colors.text.accent,
  '--cs-canvas': colors.surface.canvas,
  '--cs-canvas-soft': colors.surface.canvasSoft,
  '--cs-canvas-soft-2': colors.surface.canvasSoft2,
  '--cs-ink': colors.surface.ink,
  '--cs-hairline': colors.surface.hairline,
  '--cs-link': colors.surface.link,
  '--cs-link-deep': colors.surface.linkDeep,
  '--cs-selection-bg': colors.surface.selectionBg,
  '--cs-selection-fg': colors.surface.selectionFg,
  '--cs-primary-50': colors.primary[50],
  '--cs-primary-100': colors.primary[100],
  '--cs-primary-200': colors.primary[200],
  '--cs-primary-300': colors.primary[300],
  '--cs-primary-400': colors.primary[400],
  '--cs-primary-500': colors.primary[500],
  '--cs-primary-600': colors.primary[600],
  '--cs-primary-700': colors.primary[700],
  '--cs-primary-800': colors.primary[800],
  '--cs-primary-900': colors.primary[900],
  '--cs-brand-500': colors.brand[500],
  '--cs-brand-600': colors.brand[600],
  '--cs-gradient-develop': 'rgba(0, 124, 240, 0.2)',
  '--cs-gradient-preview': 'rgba(255, 0, 128, 0.13)',
  '--cs-gradient-cyan': 'rgba(80, 227, 194, 0.1)',
  '--cs-gradient-ship': 'rgba(249, 203, 40, 0.08)',
  '--cs-nav-blur-bg': 'rgba(10, 10, 10, 0.8)',
  '--cs-scrollbar-thumb': 'rgba(255, 255, 255, 0.2)',
  '--cs-scrollbar-thumb-hover': 'rgba(255, 255, 255, 0.35)',
  '--cs-map-ocean': colors.map.ocean,
  '--cs-map-land': colors.map.land,
};

export default { colors, cssVariables };
